import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { checkIfUrlPointsToExtraRewards } from 'core/utils/helpers';
import { fetchPlacements } from 'core/actions/ocapi';

import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import PremierOfferTileList from 'core/modules/PremierOfferTileList/PremierOfferTileList';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';

import { selectExtraRewardsPage } from 'core/selectors/app';
import { selectNavItemsFeatured } from 'core/modules/Navigation/Nav/selectors';
import {
  selectSeasonalFeaturedConfig,
  selectSeasonalFeaturedIsLoading,
  selectSeasonalFeaturedIsLoaded,
  selectSeasonalFeaturedPlacementsToDisplay,
} from './selectors';

import { triggerSeasonalFeaturedIsViewed } from './actions';

import './SeasonalFeatured.scss';

function SeasonalFeatured() {
  const config = useSelector(selectSeasonalFeaturedConfig);
  const isLoading = useSelector(selectSeasonalFeaturedIsLoading);
  const isLoaded = useSelector(selectSeasonalFeaturedIsLoaded);
  const placementsToDisplay = useSelector(selectSeasonalFeaturedPlacementsToDisplay);
  const extraRewardsPage = useSelector(selectExtraRewardsPage);
  const navItemsFeatured = useSelector(selectNavItemsFeatured);

  const dispatch = useDispatch();

  const sectionId = useRef('');
  const isInitialized = useRef(false);

  const checkIfRenderDataAvailable = () => (!isEmpty(navItemsFeatured) && !isEmpty(placementsToDisplay));

  const isRenderDataAvailable = checkIfRenderDataAvailable();

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      const { api: apiConfig } = config;
      const { params, options } = apiConfig;
      sectionId.current = options.sectionId;
      dispatch(fetchPlacements(params, options));
    }
  }, []);

  useEffect(() => {
    if (isRenderDataAvailable && !isInitialized.current) {
      isInitialized.current = true;
      const [seasonalFeaturedDeal] = navItemsFeatured;
      const { url } = seasonalFeaturedDeal;
      const isExtraRewardsPage = checkIfUrlPointsToExtraRewards(url, extraRewardsPage);

      dispatch(triggerSeasonalFeaturedIsViewed({
        seasonalFeaturedDeal,
        isExtraRewardsPage,
        placementsToDisplay,
        sectionId: sectionId.current,
      }));
    }
  }, [isInitialized.current, isRenderDataAvailable]);

  if (!isRenderDataAvailable) {
    return null;
  }

  const [seasonalFeaturedDeal] = navItemsFeatured;
  const { url, title } = seasonalFeaturedDeal;
  const isExtraRewardsPage = checkIfUrlPointsToExtraRewards(url, extraRewardsPage);

  return (
    <div className="mn_seasonalFeatured">
      <ContentWrap>
        <SectionTitle title="Top deals" />
        <div className="mn_seasonalFeaturedInner" data-is-extra-rewards={isExtraRewardsPage}>
          <div className="mn_seasonalFeaturedContent">
            <h2 className="mn_seasonalFeaturedTitle">{title}</h2>

            <PremierOfferTileList placements={placementsToDisplay} sectionId={sectionId.current} />

            <div className="mn_seasonalFeaturedAllOffers">
              <a className="mn_button" href={`${url}?isource=seasonal_featured_flyout`}>
                View all
              </a>
            </div>
          </div>
        </div>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><SeasonalFeatured {...props} /></ComponentErrorBoundary>;
